import { Product } from '@virginexperiencedays/dy';
import { useRouter } from 'next/compat/router';
import { useMemo } from 'react';
import useSWRImmutable from 'swr/immutable';

import { PersistentCampaigns as CampaignName } from '@virginexperiencedays/dy/campaign/names';
import { DYCategoryRecommendationType } from './gift-finder/categoriesConfig';

const fetcher = async ([route, params]) => {
  const res = await fetch(route, {
    method: 'POST',
    body: JSON.stringify(params),
  });

  if (!res.ok) {
    throw new Error('Error fetching recommendations');
  }

  return res.json();
};

const apiRoute = '/api/dy/recommendations';

type UseRecommendationsReturnType = {
  recommendations: Product[];
  categoryRecommendations: DYCategoryRecommendationType[];
  isLoading: boolean;
  isValidating: boolean;
  broadenResults?: number;
  hasNoResults: boolean;
  error: any;
};

type UseRecommendationsArgsType = {
  campaign?: string;
};

export const useRecommendations = (
  { campaign }: UseRecommendationsArgsType = { campaign: CampaignName.gift_finder }
): UseRecommendationsReturnType => {
  const router = useRouter();

  // memoize value to ensure that fetch happens only once, when router is ready and mounted, not when we broadenResults with shallow push
  const memoizedQuery = useMemo(() => {
    if (!router.isReady) return null;

    // filter out variation query param
    return { ...router.query, variation: undefined };
  }, [router.isReady]);

  const { data, error, isLoading, isValidating } = useSWRImmutable(
    router?.isReady ? [apiRoute, { ...memoizedQuery, campaign }] : null,
    fetcher
  );

  return {
    recommendations: data?.recommendations,
    categoryRecommendations: data?.categoryRecommendations,
    broadenResults: data?.newPriceTo,
    hasNoResults: data?.hasNoResults,
    isLoading,
    isValidating,
    error,
  };
};
